/**
 * EncycGames release profile — hide (do not delete) non–1.0 surfaces.
 * Set VITE_RELEASE_PROFILE=full to show the complete platform UI.
 * Default: v1_contest (EncycGames 1.0).
 */
export type ReleaseProfile = 'v1_contest' | 'full';

export type FeatureKey =
  | 'publicSponsors'
  | 'publicPricing'
  | 'publicTeam'
  | 'publicLeaderboard'
  | 'publicSportHub'
  | 'playerDashboard'
  | 'playerLearning'
  | 'playerRanking'
  | 'playerTrainingEval'
  | 'playerReports'
  | 'playerAiAssistant'
  | 'playerPeerBenchmark'
  | 'playerDrillsFitness'
  | 'playerContests'
  | 'playerProfile'
  | 'playerCertificates'
  | 'contestAdvancedElite'
  | 'registerSponsor';

const envProfile = String(import.meta.env.VITE_RELEASE_PROFILE || 'v1_contest').toLowerCase();

export const RELEASE_PROFILE: ReleaseProfile =
  envProfile === 'full' ? 'full' : 'v1_contest';

/** Sports exposed in EncycGames 1.0 public + player contest UI */
export const RELEASE_SPORTS = ['Football', 'Cricket', 'Badminton'] as const;

export type ReleaseSport = (typeof RELEASE_SPORTS)[number];

/** Feature map for v1_contest. When profile is `full`, all features are treated as enabled. */
export const V1_FEATURES: Record<FeatureKey, boolean> = {
  publicSponsors: false,
  publicPricing: false,
  publicTeam: false,
  publicLeaderboard: true,
  publicSportHub: false,
  playerDashboard: false,
  playerLearning: false,
  playerRanking: false,
  playerTrainingEval: false,
  playerReports: false,
  playerAiAssistant: false,
  playerPeerBenchmark: false,
  playerDrillsFitness: false,
  playerContests: true,
  playerProfile: true,
  playerCertificates: true,
  contestAdvancedElite: false,
  registerSponsor: false,
};

export function isFullRelease(): boolean {
  return RELEASE_PROFILE === 'full';
}

export function isFeatureEnabled(key: FeatureKey): boolean {
  if (isFullRelease()) return true;
  return V1_FEATURES[key];
}

export function isReleaseSport(sport: string): boolean {
  const normalized = String(sport || '').trim().toLowerCase();
  return RELEASE_SPORTS.some((s) => s.toLowerCase() === normalized);
}

export function getReleaseSports(): string[] {
  if (isFullRelease()) {
    // Imported lazily via caller using CORE_SPORTS — keep this list as 1.0 sports only;
    // callers that need full CORE_SPORTS should use CORE_SPORTS when isFullRelease().
    return [...RELEASE_SPORTS];
  }
  return [...RELEASE_SPORTS];
}

/** Default home after player login in current release profile */
export function getPlayerHomePath(): string {
  if (isFeatureEnabled('playerDashboard')) return '/player/dashboard';
  if (isFeatureEnabled('playerContests')) return '/player/contests';
  if (isFeatureEnabled('playerProfile')) return '/player/profile';
  return '/player/contests';
}

/** Where gated player deep-links redirect */
export function getPlayerFallbackPath(): string {
  return getPlayerHomePath();
}
